// lexical
import { QuoteNode, HeadingNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
// import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { ToolbarPlugin } from './toolbar';
import { IText } from 'types/domain/text';
import { ActionEntityPosition, ActionStyle } from 'types/domain/action';
import { memo, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical';
import { useAppSelector } from 'core/store/hooks';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

function Placeholder() {
  return (
    <div className="pointer-events-none w-full absolute self-center text-center opacity-60 whitespace-nowrap top-2 inline-flex justify-center items-center select-none  text-base">
      Add text here
    </div>
  );
}

const editorConfig = {
  namespace: 'MyEditor',
  onError(error) {
    console.log(error);
  },
  nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, AutoLinkNode, LinkNode],
};

export interface LexicalProps extends PropsWithChildren {
  id?: string;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  text?: IText;
  inScene: boolean;
  parentId: number;
  parentRef: string;
  position?: ActionEntityPosition;
  style?: ActionStyle;
  onChange?: (val) => void;
  onChangeColor?: (val) => void;
  onClick?: (val) => void;
  onFocus?: (val) => void;
  onBlur?: (val) => void;
  onDelete?: (val) => void;
  onExpand?: (val) => void;
  onPosition?: (val) => void;
  onStyle?: (val) => void;
}

export const LexicalTextEditor = memo(({ text, id, onChange, className, ...props }: LexicalProps) => {
  const onUpdate = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      var state = editorState.toJSON();
      editor.update(() => onChange({ html: $generateHtmlFromNodes(editor, null), htmlJson: [state?.root] }));
    },
    [onChange],
  );

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className={`flex justify-center items-center flex-col w-full relative`}>
        <ToolbarPlugin
          color={text?.color}
          setColor={props.onChangeColor}
          onDelete={props.onDelete}
          onExpand={props.onExpand}
          position={props.position}
          onPosition={props.onPosition}
          style={props.style}
          onStyle={props.onStyle}
          inScene={props.inScene}
        />
        <div className="relative w-full">
          <RichTextPlugin
            contentEditable={<ContentEditable className={`${className} lexical caret-primary `} />}
            placeholder={<Placeholder />}
            ErrorBoundary={null as any}
          />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          {/* <TabIndentationPlugin /> */}
          <OnChangePlugin ignoreHistoryMergeTagChange ignoreSelectionChange onChange={onUpdate} />
          <LoadInitialContent text={text} />
        </div>
      </div>
    </LexicalComposer>
  );
});

const LoadInitialContent = ({ text }: { text: IText }) => {
  const [editor] = useLexicalComposerContext();
  const redoIndex = useAppSelector(state => state.entity.index);
  const prevId = useRef<number>();
  const prevInd = useRef<number>();

  const update = useCallback(() => {
    if (text?.html)
      editor.update(() => {
        const root = $getRoot();
        const parser = new DOMParser();
        const dom = parser.parseFromString(text.html, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        root.clear();
        root.select();
        $insertNodes(nodes);
      });
  }, [editor, text?.html]);

  useEffect(() => {
    if (!prevInd.current || redoIndex > prevInd.current) prevInd.current = redoIndex;
    else if (redoIndex < prevInd.current && text) {
      update();
      prevInd.current = redoIndex;
    }
  }, [redoIndex, text, update]);

  useEffect(() => {
    if (!text || prevId.current === text.id) return;
    update();
    prevId.current = text.id;
  }, [editor, redoIndex, text, update]);

  return null;
};
